import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  CHAT_ATTACHMENT_ALLOWED_TYPES,
  CHAT_ATTACHMENT_MAX_BYTES,
  CHAT_ATTACHMENTS_MAX_PER_MESSAGE,
  ChatAttachment,
  ChatAttachmentInput,
  ChatAttachmentType,
  attachmentUrlExpired,
  supportAgentApi,
  uploadChatAttachment,
} from './support-agent-api';

/**
 * Chat attachments on the web (Michael, 10/14; API = pom-2-api #578).
 * Shared by the customer widget (ai-chat-widget.tsx) and the Chats inbox
 * (chats-panel.tsx): the same picker, the same pending-upload list, the
 * same rendering of files on a turn. The flow is pick → upload link →
 * PUT to S3 → send the message with `attachments` once every file is
 * `ready`. Files are only accepted while the chat is escalated: the server
 * refuses them on an AI chat (409), and the hook drops pending files when
 * the chat leaves that state or the open chat changes.
 */

export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const CHAT_ATTACHMENT_ACCEPT = [
  '.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp', '.gif', '.pdf', '.csv', '.xlsx',
  ...CHAT_ATTACHMENT_ALLOWED_TYPES,
].join(',');

export const ATTACHMENT_TYPE_ERROR = 'That file type is not supported. You can send images, PDF, CSV or Excel (.xlsx) files.';
export const ATTACHMENT_SIZE_ERROR = 'That file is too big — the limit is 10 MB.';
export const attachmentCountError = (max: number) => `You can send up to ${max} files per message.`;
export const ATTACHMENT_UPLOAD_ERROR = "Couldn't upload that file. Please try again.";
export const ATTACHMENT_NOT_ESCALATED_ERROR = 'Files can be sent once you are talking to a person.';
export const ATTACHMENT_REFUSED_ERROR = 'That file was refused by the server.';
export const ATTACHMENT_LINK_EXPIRED = 'This link has expired. Reopen the chat to get a new one.';

// When the browser reports no type (or the generic octet-stream), the
// extension decides — and only these extensions.
const EXTENSION_TYPES: Record<string, ChatAttachmentType> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  gif: 'image/gif',
  pdf: 'application/pdf',
  csv: 'text/csv',
  xlsx: XLSX_TYPE,
};
const typeForExtension = (ext: string): ChatAttachmentType | null =>
  Object.prototype.hasOwnProperty.call(EXTENSION_TYPES, ext) ? EXTENSION_TYPES[ext] : null;

export function isImageAttachment(type: string): boolean {
  return type.startsWith('image/');
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The type the server will be told, or null when the file is not one we
 * take. The same rule the server applies to the declared type (pom-2-api,
 * Pollen review 10/15), so a file is refused here for exactly the reasons
 * it would be refused there: the browser's type with any parameters
 * dropped and lower-cased when it is on the list; the extension when the
 * browser reports nothing or the generic octet-stream; Windows'
 * `application/vnd.ms-excel` only for a `.csv` (`.xls`/`.xlsm` stay
 * refused); anything else refused. What the message finally sends is the
 * upload link's `type`, not this — see usePendingAttachments.
 */
export function attachmentTypeFor(file: Pick<File, 'name' | 'type'>): ChatAttachmentType | null {
  const reported = (file.type || '').split(';')[0].trim().toLowerCase();
  if ((CHAT_ATTACHMENT_ALLOWED_TYPES as readonly string[]).includes(reported)) {
    return reported as ChatAttachmentType;
  }
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
  if (reported === '' || reported === 'application/octet-stream') return typeForExtension(ext);
  if (reported === 'application/vnd.ms-excel' && ext === 'csv') return 'text/csv';
  return null;
}

export interface ValidatedFile {
  file: File;
  type: ChatAttachmentType;
}

export function validateAttachmentFiles(
  files: File[],
  alreadyPending: number,
): { accepted: ValidatedFile[]; errors: string[] } {
  const accepted: ValidatedFile[] = [];
  const errors: string[] = [];
  for (const file of files) {
    const type = attachmentTypeFor(file);
    if (!type) {
      errors.push(ATTACHMENT_TYPE_ERROR);
      continue;
    }
    if (file.size <= 0 || file.size > CHAT_ATTACHMENT_MAX_BYTES) {
      errors.push(ATTACHMENT_SIZE_ERROR);
      continue;
    }
    if (alreadyPending + accepted.length >= CHAT_ATTACHMENTS_MAX_PER_MESSAGE) {
      errors.push(attachmentCountError(CHAT_ATTACHMENTS_MAX_PER_MESSAGE));
      break;
    }
    accepted.push({ file, type });
  }
  return { accepted, errors: Array.from(new Set(errors)) };
}

export interface PendingAttachment {
  id: string;
  file: File;
  name: string;
  type: ChatAttachmentType;
  size: number;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  key?: string;
  error?: string;
}

function uploadErrorCopy(err: any): string {
  const status = err?.response?.status;
  const code = err?.response?.data?.message;
  if (status === 409) return ATTACHMENT_NOT_ESCALATED_ERROR;
  if (status === 400) return typeof code === 'string' && code.includes('10MB') ? ATTACHMENT_SIZE_ERROR : ATTACHMENT_REFUSED_ERROR;
  return ATTACHMENT_UPLOAD_ERROR;
}

export interface PendingAttachments {
  items: PendingAttachment[];
  /** The last validation or upload problem worth telling the user, if any. */
  error: string | null;
  add: (files: File[]) => void;
  remove: (id: string) => void;
  retry: (id: string) => void;
  clear: () => void;
  /** What a message can send right now: every file that finished uploading. */
  ready: ChatAttachmentInput[];
  /** True while any file is still uploading or has failed: the message cannot be sent yet. */
  blocked: boolean;
}

/**
 * The composer's pending files for one chat. Each file uploads as soon as it
 * is picked; the message is sendable once every file is `ready`. Pending
 * files are dropped when `enabled` turns false (the chat left `escalated`)
 * or `chatId` changes.
 */
export function usePendingAttachments({ chatId, enabled }: { chatId: string | undefined; enabled: boolean }): PendingAttachments {
  const [items, setItems] = useState<PendingAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const itemsRef = useRef<PendingAttachment[]>([]);
  itemsRef.current = items;
  const chatIdRef = useRef(chatId);
  chatIdRef.current = chatId;
  const seqRef = useRef(0);

  const patch = (id: string, changes: Partial<PendingAttachment>) =>
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));

  const upload = useCallback(async (item: PendingAttachment) => {
    const id = chatIdRef.current;
    if (!id) {
      patch(item.id, { status: 'error', error: ATTACHMENT_UPLOAD_ERROR });
      return;
    }
    // The outcome belongs to the chat the file was picked on. Once the
    // composer has moved to another chat, or the file is gone (removed, or
    // dropped with the chat leaving escalated), nothing is reported — an
    // error least of all, which would otherwise land on the new chat
    // (Pollen review 10/15).
    const stillWanted = () => chatIdRef.current === id && itemsRef.current.some((candidate) => candidate.id === item.id);
    patch(item.id, { status: 'uploading', progress: 0, error: undefined });
    try {
      const link = await supportAgentApi.createChatAttachmentUploadLink(id, { name: item.name, type: item.type, size: item.size });
      if (!stillWanted()) return;
      await uploadChatAttachment(link, item.file, (fraction) => {
        if (stillWanted()) patch(item.id, { progress: fraction });
      });
      if (!stillWanted()) return;
      // The link's name/type/size are the server's canonical ones (its
      // type rule, its sanitised name) and are what the message sends.
      patch(item.id, { status: 'ready', progress: 1, key: link.key, name: link.name, type: link.type as ChatAttachmentType, size: link.size });
    } catch (err: any) {
      if (!stillWanted()) return;
      const copy = uploadErrorCopy(err);
      patch(item.id, { status: 'error', error: copy });
      setError(copy);
    }
  }, []);

  const add = useCallback(
    (files: File[]) => {
      setError(null);
      const { accepted, errors } = validateAttachmentFiles(files, itemsRef.current.length);
      if (errors.length) setError(errors[0]);
      if (!accepted.length) return;
      const created: PendingAttachment[] = accepted.map(({ file, type }) => ({
        id: `${Date.now()}-${++seqRef.current}`,
        file,
        name: file.name,
        type,
        size: file.size,
        status: 'uploading',
        progress: 0,
      }));
      setItems((prev) => [...prev, ...created]);
      created.forEach((item) => void upload(item));
    },
    [upload],
  );

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    setError(null);
  }, []);
  const retry = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((candidate) => candidate.id === id);
      if (item) void upload(item);
    },
    [upload],
  );
  const clear = useCallback(() => {
    setItems([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (!enabled) clear();
  }, [enabled, clear]);
  useEffect(() => {
    clear();
  }, [chatId, clear]);

  const ready = items
    .filter((item) => item.status === 'ready' && item.key)
    .map((item) => ({ key: item.key as string, name: item.name, type: item.type, size: item.size }));
  const blocked = items.some((item) => item.status !== 'ready');
  return { items, error, add, remove, retry, clear, ready, blocked };
}

export const ATTACH_FILE_LABEL = 'Attach a file';

/** The paperclip: a hidden multi-file input behind an icon button. */
export const AttachmentPickerButton: React.FC<{ disabled?: boolean; onFiles: (files: File[]) => void }> = ({ disabled, onFiles }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={CHAT_ATTACHMENT_ACCEPT}
        hidden
        data-testid="chat-attachment-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
      <Tooltip title={ATTACH_FILE_LABEL}>
        <span>
          <IconButton aria-label={ATTACH_FILE_LABEL} disabled={disabled} onClick={() => inputRef.current?.click()}>
            <AttachFileIcon />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};

/** Files picked but not yet sent: progress while uploading, retry on failure, remove any time. */
export const PendingAttachmentList: React.FC<{
  items: PendingAttachment[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  disabled?: boolean;
}> = ({ items, onRemove, onRetry, disabled }) => {
  if (!items.length) return null;
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }} data-testid="chat-pending-attachments">
      {items.map((item) => (
        <Chip
          key={item.id}
          data-testid="chat-pending-attachment"
          data-status={item.status}
          size="small"
          color={item.status === 'error' ? 'error' : 'default'}
          variant="outlined"
          icon={
            item.status === 'uploading' ? (
              <CircularProgress size={14} variant={item.progress > 0 ? 'determinate' : 'indeterminate'} value={item.progress * 100} />
            ) : item.status === 'error' ? (
              <ReplayIcon />
            ) : (
              <InsertDriveFileOutlinedIcon />
            )
          }
          label={item.status === 'error' ? `${item.name} · ${item.error ?? ATTACHMENT_UPLOAD_ERROR}` : `${item.name} · ${formatBytes(item.size)}`}
          onClick={item.status === 'error' ? () => onRetry(item.id) : undefined}
          onDelete={disabled ? undefined : () => onRemove(item.id)}
          deleteIcon={<span aria-label={`Remove ${item.name}`}>×</span>}
        />
      ))}
    </Stack>
  );
};

// ── Signed url cache ───────────────────────────────────────────────────────
// Every read of a chat mints new signed urls (15 min), so without this each
// poll would hand every <img> a new src and the browser would download the
// whole transcript's images again every few seconds (Pollen review 10/15).
// Keyed by the attachment's `key`: the first url seen is kept until it is
// within a minute of expiring, then the newly delivered one takes over.
// Page lifetime only; expired entries are dropped whenever one is added.
const URL_REUSE_MARGIN_MS = 60_000;
interface CachedUrl {
  url: string;
  urlExpiresAt: string;
  expiresAtMs: number;
}
const urlCache = new Map<string, CachedUrl>();

function pruneUrlCache(now: number) {
  urlCache.forEach((entry, key) => {
    if (entry.expiresAtMs <= now) urlCache.delete(key);
  });
}

/**
 * The attachment with the url to render and open: the cached one while it
 * is more than a minute from expiry, otherwise the delivered one (which
 * then becomes the cached one). The same object comes back when nothing
 * changes, so a render sees the same `src` it saw last time.
 */
export function resolveAttachmentUrl<T extends ChatAttachment>(attachment: T, now = Date.now()): T {
  const cached = urlCache.get(attachment.key);
  if (cached && cached.expiresAtMs - now > URL_REUSE_MARGIN_MS) {
    return cached.url === attachment.url ? attachment : { ...attachment, url: cached.url, urlExpiresAt: cached.urlExpiresAt };
  }
  const expiresAtMs = attachment.urlExpiresAt ? new Date(attachment.urlExpiresAt).getTime() : NaN;
  if (attachment.url && Number.isFinite(expiresAtMs) && expiresAtMs > now) {
    pruneUrlCache(now);
    urlCache.set(attachment.key, { url: attachment.url, urlExpiresAt: attachment.urlExpiresAt as string, expiresAtMs });
    return attachment;
  }
  // Nothing usable was delivered (no url, or one already expired): a cached
  // url with some life left still beats that.
  if (cached && cached.expiresAtMs > now) {
    return { ...attachment, url: cached.url, urlExpiresAt: cached.urlExpiresAt };
  }
  return attachment;
}

// Test-only handle (as reverse-geocode-cache.ts does): suites clear it so a
// url cached by one test does not stand in for another's.
export const __attachmentUrlCacheForTests = process.env.NODE_ENV === 'test' ? urlCache : undefined;

/**
 * Files on a sent turn. Images show as a thumbnail (click → full size in a
 * new tab); everything else as a file chip linking to the download. A file
 * without a url is listed by name, disabled. Every url goes through the
 * cache above, so a poll does not re-download the images. When the signed
 * url has expired, `refresh` is asked for a fresh one before opening; if
 * none comes back nothing opens and the file says so. An image the browser
 * cannot render (HEIC outside Safari, an expired url) falls back to the
 * chip rather than a broken-image icon, and is tried again once a new url
 * arrives for it (Interrogator review).
 */
export const AttachmentList: React.FC<{
  attachments: ChatAttachment[];
  refresh?: (attachment: ChatAttachment) => Promise<ChatAttachment | undefined>;
  align?: 'flex-start' | 'flex-end';
}> = ({ attachments, refresh, align = 'flex-start' }) => {
  // Both keyed by attachment key, remembering the url they happened with:
  // a different url for the same key retries the image and drops the
  // expired notice by itself.
  const [unrenderable, setUnrenderable] = useState<Record<string, string>>({});
  const [expired, setExpired] = useState<Record<string, string>>({});
  const noteExpired = (attachment: ChatAttachment) => setExpired((prev) => ({ ...prev, [attachment.key]: attachment.url ?? '' }));
  const forgetExpired = (key: string) =>
    setExpired((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _gone, ...rest } = prev;
      return rest;
    });

  const open = async (attachment: ChatAttachment) => {
    forgetExpired(attachment.key);
    if (attachment.url && !attachmentUrlExpired(attachment)) {
      window.open(attachment.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!refresh) {
      noteExpired(attachment);
      return;
    }
    // The fresh url only arrives after a round trip, and Safari blocks a
    // window.open that is not made on the click itself. So the tab opens
    // now, blank, and is pointed at the file once we have it. `noopener` in
    // the features would make window.open return null, so the opener is
    // cut by hand before the tab goes anywhere.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    let fresh: ChatAttachment | undefined;
    try {
      fresh = await refresh(attachment);
    } catch {
      fresh = undefined;
    }
    const url = fresh?.url && !attachmentUrlExpired(fresh) ? resolveAttachmentUrl(fresh).url : undefined;
    if (!url) {
      // Never the expired url: S3 would only answer "Request has expired".
      tab?.close();
      noteExpired(attachment);
      return;
    }
    if (tab) tab.location.href = url;
    else window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!attachments.length) return null;
  return (
    <Stack spacing={0.5} alignItems={align} data-testid="chat-attachments">
      {attachments.map((delivered) => {
        const attachment = resolveAttachmentUrl(delivered);
        const url = attachment.url ?? '';
        const asImage = isImageAttachment(attachment.type) && Boolean(attachment.url) && unrenderable[attachment.key] !== url;
        return (
          <Stack key={attachment.key} spacing={0.25} alignItems={align} sx={{ maxWidth: '100%' }}>
            {asImage ? (
              <ButtonBase
                onClick={() => void open(attachment)}
                aria-label={`Open ${attachment.name}`}
                sx={{ borderRadius: 1, overflow: 'hidden' }}
              >
                <Box
                  component="img"
                  src={attachment.url}
                  alt={attachment.name}
                  data-testid="chat-attachment-image"
                  onError={() => setUnrenderable((prev) => ({ ...prev, [attachment.key]: url }))}
                  sx={{ display: 'block', maxWidth: 220, maxHeight: 180, objectFit: 'cover' }}
                />
              </ButtonBase>
            ) : (
              <Chip
                data-testid="chat-attachment-file"
                size="small"
                icon={<InsertDriveFileOutlinedIcon />}
                label={`${attachment.name} · ${formatBytes(attachment.size)}`}
                clickable={Boolean(attachment.url)}
                disabled={!attachment.url}
                onClick={attachment.url ? () => void open(attachment) : undefined}
                sx={{ maxWidth: '100%', bgcolor: 'background.paper', color: 'text.primary' }}
              />
            )}
            {expired[attachment.key] === url && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }} data-testid="chat-attachment-expired">
                {ATTACHMENT_LINK_EXPIRED}
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
